import { PKR, USD, resolveAmount, formatMoney } from "./currency";

// A product either sells at its own price, or offers named options such as
// "Standard Quality" / "Premium Quality" that each carry their own price and
// stock flag. Every price exists twice: PKR, and the USD figure the admin
// entered for international visitors.

export const getVariants = (product) => {
  const list = product && product.variants;
  if (!Array.isArray(list)) return [];
  return list
    .map((variant) => ({
      name: String(variant && variant.name ? variant.name : "").trim(),
      price: Number(variant && variant.price),
      price_usd: variant && variant.price_usd !== undefined ? variant.price_usd : null,
      in_stock: variant && variant.in_stock === "no" ? "no" : "yes",
    }))
    .filter((variant) => variant.name !== "" && Number.isFinite(variant.price) && variant.price >= 0);
};

export const hasVariants = (product) => getVariants(product).length > 0;

export const getSellableVariants = (product) =>
  getVariants(product).filter((variant) => variant.in_stock === "yes");

// A product with options is buyable when at least one option is in stock;
// otherwise it falls back to the product's own stock flag.
export const isProductBuyable = (product) => {
  if (!product) return false;
  if (hasVariants(product)) return getSellableVariants(product).length > 0;
  return product.in_stock === "yes";
};

// The option a customer lands on: the first one the admin listed. If that one
// happens to be sold out, fall back to the first that is still available so the
// page never opens on a selection nobody can buy.
export const getDefaultVariant = (product) => {
  const variants = getVariants(product);
  if (!variants.length) return null;
  if (variants[0].in_stock === "yes") return variants[0];
  return getSellableVariants(product)[0] || variants[0];
};

// The price actually charged for a given selection, read from the column for
// the visitor's currency. `available` is false when that column is empty, and
// the caller shows the contact line instead of a figure.
export const getUnitPrice = (product, variant, currency = PKR) => {
  if (variant) return resolveAmount(variant.price, variant.price_usd, currency);
  return resolveAmount(product && product.price, product && product.price_usd, currency);
};

// Per-product delivery charge, set separately for each currency.
export const getShipping = (product, currency = PKR) =>
  resolveAmount(product && product.shipping_pkr, product && product.shipping_usd, currency);

// What a listing card shows. `from` is true when the single figure shown does
// not tell the whole story - either the options differ in price, or some of
// them carry no price in this currency at all.
export const getPriceRange = (product, currency = PKR) => {
  const variants = getVariants(product);
  const sellable = getSellableVariants(product);
  const pool = sellable.length ? sellable : variants;

  if (!pool.length) {
    const base = resolveAmount(product && product.price, product && product.price_usd, currency);
    return { min: base.amount, max: base.amount, from: false, currency, available: base.available };
  }

  // Only options actually priced in this currency can go into the range.
  // Prices are never converted, so an option with no USD price contributes
  // nothing to a USD range rather than dragging a PKR figure into it.
  const priced = pool
    .map((variant) => resolveAmount(variant.price, variant.price_usd, currency))
    .filter((entry) => entry.available);

  if (!priced.length) {
    return { min: null, max: null, from: false, currency, available: false };
  }

  const amounts = priced.map((entry) => entry.amount);
  const min = Math.min(...amounts);
  const max = Math.max(...amounts);
  return {
    min,
    max,
    from: max > min || priced.length < pool.length,
    currency,
    available: true,
  };
};

// Delivery is charged per product, once, however many units or options of it
// are in the basket. Cart and checkout both call this so they cannot drift
// apart on what the customer is asked to pay.
export const getCartShipping = (items) => {
  const counted = new Set();
  return (items || []).reduce((total, item) => {
    if (counted.has(item.id)) return total;
    counted.add(item.id);
    const fee = Number(item.shipping_pkr);
    return total + (Number.isFinite(fee) && fee > 0 ? fee : 0);
  }, 0);
};

// Cart line identity. The same product in two different options has to be two
// separate lines, so the option name is part of the key.
export const cartLineId = (productId, variantName) =>
  variantName ? `${productId}::${variantName}` : String(productId);

// Name shown in the cart, the order email and the dashboard order detail.
export const lineLabel = (title, variantName) =>
  variantName ? `${title} - ${variantName}` : title;

// Formats a {amount, currency} pair straight from the helpers above
export const formatPriceRange = (range) => formatMoney(range.min, range.currency);

export { PKR, USD, formatMoney };
